import { useCallback, useEffect, useRef, useState } from 'react';
import { getTransactionStatus as getSVMTransactionStatus } from '../../chains/svm/api';
import { getTransactionStatus as getEVMTransactionStatus } from '../../chains/evm/api';
import { isEVMChain } from '../../wallet/chains';
import { WEB3_ADDRESS } from '../../wallet';
import { isBrowser, jsonSafeNftMetadata, readTxHistoryFromStorage, TX_HISTORY_KEY, } from '../utils/storage';
export function useTransactionHistory({ walletConnection, toast, transactionHistoryApi, walletSession, }) {
    const [history, setHistory] = useState(() => readTxHistoryFromStorage());
    const timers = useRef({});
    const txHistoryRef = useRef({});
    const apiTrackTimers = useRef({});
    const txIdToLink = useCallback((txId) => {
        const network = walletConnection.network;
        const chainInfo = network ? WEB3_ADDRESS[network] : undefined;
        if (!chainInfo)
            return txId;
        return `${chainInfo.explorer}/tx/${txId}?chainid=${chainInfo.chainId}`;
    }, [walletConnection.network]);
    useEffect(() => {
        return () => {
            const pending = timers.current;
            Object.keys(pending).forEach((hash) => {
                clearInterval(pending[hash]);
            });
            timers.current = {};
        };
    }, []);
    useEffect(() => {
        if (!isBrowser)
            return;
        try {
            localStorage.setItem(TX_HISTORY_KEY, JSON.stringify(history));
        }
        catch {
            // Ignore storage write failures (e.g., private mode/storage limits).
        }
    }, [history]);
    const addTransaction = useCallback((chain, hash, action, metadata) => {
        if (transactionHistoryApi) {
            const walletAddress = walletConnection?.network === chain ? walletConnection?.address : '';
            if (!walletAddress)
                return;
            if (!transactionHistoryApi.hasAuthToken()) {
                toast.info('Sign in to save transaction history to your account.');
                return;
            }
            const link = txIdToLink(hash);
            const existing = transactionHistoryApi.history[chain] ?? [];
            const index = existing.length;
            const safeMetadata = Array.isArray(metadata)
                ? metadata.map((info) => jsonSafeNftMetadata(info))
                : metadata;
            transactionHistoryApi.createTransaction({
                chain,
                walletAddress,
                hash,
                action,
                metadata: safeMetadata,
                link,
                index,
            });
            return;
        }
        setHistory((prev) => {
            const current = prev[chain];
            return {
                ...prev,
                [chain]: [
                    ...(current ?? []),
                    {
                        hash,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        status: 'Pending',
                        action,
                        metadata,
                        link: txIdToLink(hash),
                        index: current?.length ?? 0,
                    },
                ],
            };
        });
    }, [transactionHistoryApi, walletConnection, toast, txIdToLink]);
    const trackTransaction = useCallback((chainFilter) => {
        if (transactionHistoryApi) {
            const hist = txHistoryRef.current;
            const chainKeys = chainFilter ? [chainFilter] : Object.keys(hist);
            chainKeys.forEach((chainKey) => {
                const list = hist[chainKey];
                if (!Array.isArray(list))
                    return;
                list.forEach((tx) => {
                    if (tx.status === 'Success' || tx.status === 'Failed')
                        return;
                    const txChain = (tx.chain ?? chainKey);
                    const timerKey = `${txChain}:${tx.hash}`;
                    if (apiTrackTimers.current[timerKey])
                        return;
                    apiTrackTimers.current[timerKey] = globalThis.setInterval(async () => {
                        try {
                            const fresh = txHistoryRef.current[txChain] ?? [];
                            const row = fresh.find((t) => t.hash === tx.hash);
                            if (!row || row.status === 'Success' || row.status === 'Failed') {
                                clearInterval(apiTrackTimers.current[timerKey]);
                                delete apiTrackTimers.current[timerKey];
                                return;
                            }
                            const nextStatus = isEVMChain(txChain)
                                ? await getEVMTransactionStatus(txChain, row.hash)
                                : await getSVMTransactionStatus(row.hash);
                            if (row.status === nextStatus)
                                return;
                            const authed = transactionHistoryApi.hasAuthToken();
                            const rowWithId = row;
                            if (rowWithId._id && authed) {
                                try {
                                    await transactionHistoryApi.updateTransaction(rowWithId._id, {
                                        status: nextStatus,
                                    });
                                }
                                catch {
                                    transactionHistoryApi.patchTransactionStatus({
                                        chain: txChain,
                                        hash: row.hash,
                                        status: nextStatus,
                                        updatedAt: new Date().toISOString(),
                                    });
                                }
                            }
                            else {
                                transactionHistoryApi.patchTransactionStatus({
                                    chain: txChain,
                                    hash: row.hash,
                                    status: nextStatus,
                                    updatedAt: new Date().toISOString(),
                                });
                            }
                            if (nextStatus === 'Success' || nextStatus === 'Failed') {
                                clearInterval(apiTrackTimers.current[timerKey]);
                                delete apiTrackTimers.current[timerKey];
                            }
                        }
                        catch {
                            // keep polling
                        }
                    }, 3000);
                });
            });
            return;
        }
        (history[chainFilter] ?? []).forEach((tx) => {
            const status = tx.status;
            if (status == 'Success' || status === 'Failed')
                return;
            if (timers.current[tx.hash])
                return;
            timers.current[tx.hash] = globalThis.setInterval(async () => {
                try {
                    const nextStatus = isEVMChain(chainFilter)
                        ? await getEVMTransactionStatus(chainFilter, tx.hash)
                        : await getSVMTransactionStatus(tx.hash);
                    setHistory((prev) => {
                        const current = prev[chainFilter];
                        if (!current || tx.index < 0 || tx.index >= current.length)
                            return prev;
                        if (current[tx.index].status === nextStatus)
                            return prev;
                        const next = [...current];
                        next[tx.index] = {
                            ...current[tx.index],
                            status: nextStatus,
                            updatedAt: new Date(),
                        };
                        return {
                            ...prev,
                            [chainFilter]: next,
                        };
                    });
                    if (nextStatus === 'Success' || nextStatus === 'Failed') {
                        clearInterval(timers.current[tx.hash]);
                        delete timers.current[tx.hash];
                    }
                }
                catch {
                    // keep polling
                }
            }, 3000);
        });
    }, [history, transactionHistoryApi]);
    const resolvedHistory = transactionHistoryApi?.history ?? history;
    txHistoryRef.current = resolvedHistory;
    useEffect(() => {
        if (!transactionHistoryApi || !walletSession)
            return;
        if (walletSession.loginStatus !== 'success' || !walletSession.user?.walletAddress) {
            Object.keys(apiTrackTimers.current).forEach((k) => {
                clearInterval(apiTrackTimers.current[k]);
                delete apiTrackTimers.current[k];
            });
            return;
        }
        trackTransaction();
    }, [
        walletSession?.loginStatus,
        walletSession?.user?.walletAddress,
        resolvedHistory,
        trackTransaction,
        transactionHistoryApi,
    ]);
    useEffect(() => {
        return () => {
            Object.keys(apiTrackTimers.current).forEach((k) => {
                clearInterval(apiTrackTimers.current[k]);
                delete apiTrackTimers.current[k];
            });
        };
    }, []);
    return {
        history: resolvedHistory,
        txHistoryStatus: transactionHistoryApi ? 'synced' : 'idle',
        addTransaction,
        trackTransaction,
        txIdToLink,
    };
}
